const DEFAULT_BOOKING_TIME_ZONE = "Europe/Berlin";
const GERMAN_BOOKING_TIME_ZONE = "Europe/Berlin";

export function hasBookingTimeZone(
  bookingTimeZone?: string | null
): boolean {
  return !!bookingTimeZone;
}

export function getBookingTimeZone(
  bookingTimeZone?: string | null
): string {
  return bookingTimeZone || DEFAULT_BOOKING_TIME_ZONE;
}

export function formatBookingDateTime(
  value: string | Date | number,
  bookingTimeZone?: string | null,
  assumeTimezoneLessUtc = true
): string {
  const date = parseBookingDateValue(value, assumeTimezoneLessUtc);
  const timeZone = getBookingTimeZone(bookingTimeZone);

  try {
    return new Intl.DateTimeFormat("de-DE", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("de-DE", {
      timeZone: DEFAULT_BOOKING_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  }
}

function parseBookingDateValue(
  value: string | Date | number,
  assumeTimezoneLessUtc: boolean
): Date {
  if (
    typeof value === "string" &&
    assumeTimezoneLessUtc &&
    isIsoDateTimeWithoutTimeZone(value)
  ) {
    return new Date(`${value}Z`);
  }

  return new Date(value);
}

function isIsoDateTimeWithoutTimeZone(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) &&
    !/(Z|[+-]\d{2}:?\d{2})$/i.test(value)
  );
}

export function formatBookingWallTimeForApi(
  value: string,
  bookingTimeZone?: string | null
): string {
  const parts = parseIsoWallTime(value);
  const timeZone = getBookingTimeZone(bookingTimeZone);
  const localTimestamp = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  let offset = getTimeZoneOffsetMinutes(new Date(localTimestamp), timeZone);
  offset = getTimeZoneOffsetMinutes(
    new Date(localTimestamp - offset * 60000),
    timeZone
  );

  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(
    parts.hour
  )}:${pad(parts.minute)}:${pad(parts.second)}${formatOffset(offset)}`;
}

export function formatBookingWallTimeInZone(
  value: string,
  bookingTimeZone?: string | null
): string {
  return formatBookingDateTime(
    formatBookingWallTimeForApi(value, bookingTimeZone),
    bookingTimeZone
  );
}

export function formatBookingWallTimeInGermanTime(
  value: string,
  bookingTimeZone?: string | null
): string {
  return formatBookingDateTime(
    formatBookingWallTimeForApi(value, bookingTimeZone),
    GERMAN_BOOKING_TIME_ZONE
  );
}

export function isGermanBookingTimeZone(
  bookingTimeZone?: string | null
): boolean {
  return getBookingTimeZone(bookingTimeZone) === GERMAN_BOOKING_TIME_ZONE;
}

function parseIsoWallTime(value: string): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/
  );

  if (!match) {
    throw new Error(`Invalid booking date time: ${value}`);
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? 0),
  };
}

function getTimeZoneOffsetMinutes(date: Date, timeZone: string): number {
  const parts = getTimeZoneParts(date, timeZone);
  const timeZoneAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  return Math.round((timeZoneAsUtc - date.getTime()) / 60000);
}

function getTimeZoneParts(
  date: Date,
  timeZone: string
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const values = new Intl.DateTimeFormat("de-DE", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .formatToParts(date)
    .reduce((result, part) => {
      if (part.type !== "literal") {
        result[part.type] = Number(part.value);
      }
      return result;
    }, {} as Record<string, number>);

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour,
    minute: values.minute,
    second: values.second,
  };
}

function formatOffset(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absolute = Math.abs(offsetMinutes);
  return `${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}
